import apiClient from './client'

export interface Notification {
  id: number
  type: string
  data: {
    title: string
    message: string
    policy_id?: number
    policy_number?: string
    claim_id?: number
    claim_number?: string
    claim_type?: string
    customer_name?: string
    graphite_ref?: string
    job_id?: number
    file_name?: string
    [key: string]: any
  }
  action: string | null
  read_at: string | null
  created_at: string
  time_ago: string
}

export interface NotificationQuery {
  /** Return only unread rows. Applied server-side so the list and the count agree. */
  unreadOnly?: boolean
  /** Substring match on title, message, policy number, client name, claim type/number and type. */
  search?: string
  /** Exact notification type filter (e.g. 'claim_mention'). Applied server-side. */
  type?: string
  /** Rows per request, 1..100 (server cap). */
  limit?: number
  /** Rows to skip. */
  offset?: number
}

export interface NotificationListResponse {
  data: Notification[]
  /** The user's TRUE unread total — unaffected by search or the unread/type filter. Drives the badge. */
  unread_count: number
  /** TRUE unread @mention total (type = claim_mention), unaffected by the active filter. Drives the "Mentions" chip. */
  unread_mentions_count: number
  /** Rows matching the current filter + search. Drives "Showing X of N". */
  total: number
  has_more: boolean
  limit: number
  offset: number
}

export async function fetchNotifications(query: NotificationQuery = {}): Promise<NotificationListResponse> {
  const params: Record<string, string | number> = { limit: query.limit ?? 20 }
  if (query.unreadOnly) params.unread_only = 1
  if (query.search?.trim()) params.search = query.search.trim()
  if (query.type?.trim()) params.type = query.type.trim()
  if (query.offset) params.offset = query.offset

  const { data } = await apiClient.get<NotificationListResponse>('/notifications', { params })
  return data
}

export async function markNotificationRead(id: number): Promise<void> {
  await apiClient.put(`/notifications/${id}/read`)
}

export async function markNotificationUnread(id: number): Promise<void> {
  await apiClient.put(`/notifications/${id}/unread`)
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.post('/notifications/mark-all-read')
}
